import { useState } from 'react';
import { Button } from '../components/Button';
import { useDb } from '../db/hooks';
import { signOut } from '../auth/session';
import { recordConsent } from './consent';

function Check({ checked, onChange, children }: { checked: boolean; onChange: (checked: boolean) => void; children: React.ReactNode }) {
  return (
    <label className="flex cursor-pointer gap-3 rounded-lg border border-clinical-line p-3 text-sm">
      <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[rgb(var(--rf-primary))]" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

export function DisclaimerPage({ onAccepted }: { onAccepted: () => Promise<void> }) {
  const db = useDb();
  const [disclaimer, setDisclaimer] = useState(false);
  const [healthData, setHealthData] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await recordConsent(db);
      await onAccepted();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
      setBusy(false);
    }
  }

  return (
    <div className="safe-top mx-auto max-w-lg px-4 py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Before you start</h1>
      <div className="mt-5 space-y-3 rounded-xl border border-clinical-line bg-clinical-card p-5 text-sm leading-6 shadow-soft">
        <p>
          <strong>RenalFlow is a personal record-keeping tool.</strong> It is not a medical device and does not give medical advice, diagnose
          anything, or recommend doses.
        </p>
        <p>Reference ranges shown with lab results are general. Your lab’s printed range and your care team’s advice always come first.</p>
        <p>
          <strong>In an emergency, call your local emergency number</strong> or contact your dialysis unit straight away. Don’t wait to log it
          here.
        </p>
        <p>
          What you log is saved on this device and synced to your RenalFlow account so it’s on all your devices and backed up. It’s encrypted
          in transit and at rest, never sold or shared, and you can export or delete it at any time.
        </p>
      </div>

      <div className="mt-5 space-y-3">
        <Check checked={disclaimer} onChange={setDisclaimer}>
          I understand RenalFlow is a personal log, not medical advice.
        </Check>
        <Check checked={healthData} onChange={setHealthData}>
          I agree to RenalFlow storing the health information I enter in my account, as described in the{' '}
          <a href="https://getrenalflow.com/privacy" target="_blank" rel="noreferrer" className="font-medium text-clinical-primary underline">
            privacy policy
          </a>
          .
        </Check>
      </div>

      {error ? <p className="mt-4 text-sm text-clinical-danger">{error}</p> : null}

      <div className="mt-6 flex flex-col gap-2">
        <Button onClick={() => void accept()} disabled={!disclaimer || !healthData || busy}>
          {busy ? 'Saving…' : 'Continue'}
        </Button>
        <Button variant="ghost" onClick={() => void signOut()} disabled={busy}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
